/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  Component,
  OnInit
}                                from '@angular/core';

import {
  Router,
  ActivatedRoute,
  ParamMap, Params
} from '@angular/router';

import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
}                                from '@angular/forms';

import { first                 } from 'rxjs/operators';

import { Logger                } from '@/util/logger';
import { AuthenticationService } from '@/services/authentication.service';
import { AlertService          } from '@/services/alert.service';
import { getUrlParams }          from '@/util/util';

import { environment } from '../../../environments/environment';

@Component({
  templateUrl: 'login.component.html'
})

/**
 *
 * @class LoginComponent
 *
 * @brief we show the login page when there is no established user session
 *
 * Angular tutorial on login / registration.
 * @link https://jasonwatmore.com/post/2018/10/29/angular-7-user-registration-and-login-example-tutorial#app-component-html
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class LoginComponent implements OnInit {

  /** use a rand background image for the page */
  public backgrounds: string[] = ['cnh-01.png', 'cnh-02.png', 'cnh-03.png', 'cnh-04.png'];
  public background: string    = '';

  /** the external links for jumping to an OAuth delegated login */
  public google_sso: string    = environment.google_sso;
  public microsoft_sso: string = environment.microsoft_sso;

  /** form validation */
  public loginForm: UntypedFormGroup;

  /** flag to tell the form when we are processing */
  public loading:   boolean   = false;

  /* flag to tell the form that we completed an authentication request */
  public submitted: boolean   = false;

  /** where to return to once authenticated */
  public returnUrl: string    = '/';

  /** when we finalize SSO login, we have to authenticate with a one-time code instead of userid/password */
  public authCode: string = '';

  /** if we get redirected to login, *and* we had query parameters, try to pass 'em along */
  public queryParams: any;

  /** standard constructor */
  constructor(
    private formBuilder:  UntypedFormBuilder,
    private route:        ActivatedRoute,
    private router:       Router,
    private auth:         AuthenticationService,
    private alerts:       AlertService
  ) {

    /* pick a random background */

    this.background = this.backgrounds[Math.floor(Math.random() * this.backgrounds.length)];

    Logger.debug('[Login] checking if already logged in...');

    this.returnUrl   = this.route.snapshot.queryParamMap.get('returnUrl') || '/';
    this.queryParams = getUrlParams(this.route.snapshot.url.join(''));
    this.authCode    = this.route.snapshot.queryParamMap.get('auth_code') || '';

    /* use the web browser to pull out the 'uri' of the return path */

    const parser     = document.createElement('a');
    parser.href      = this.returnUrl;
    this.returnUrl   = parser.pathname;

    /* if we have an authcode, then we can try to auto-signin that SSO user ... */

    if (this.authCode.length > 0) {

      Logger.debug('[Login] authenticating: ', this.authCode);

      this.auth.loginSSO(this.authCode)
        .pipe(first())
        .subscribe(
          data => {

            Logger.debug('[Login] authenticated! returning to: /ide :', data);

            this.router.navigate(['/ide']);
          },
          error => {

            this.alerts.error(error.error.message, {autoClose: true});

            Logger.error('[Login] authentication error: ', error.error.message);

            this.loading = false;
          });
    }

    /* are we already logged in? */

    if (this.auth.userOfSession) {

      Logger.debug('[Login] already logged in, going back to ', this.returnUrl, this.queryParams);

      this.router.navigate(
        [this.returnUrl],
        {
          queryParams: this.queryParams,
          queryParamsHandling: 'preserve'
        });
    }
  }

  /** on page setup, create the form validator */
  ngOnInit() {

    Logger.debug('[Login] init...');

    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    /* return to - should be where they were before, or home. */

    Logger.debug('[Login] will return to: ', this.returnUrl, this.queryParams);
  }

  /** easy access to form fields */
  get f() {
    return this.loginForm.controls;
  }

  /** form submission (authentication attempt) */
  onSubmit() {

    this.submitted = true;

    /* do we have all fields? */

    if (this.loginForm.invalid) {

      return;
    }

    this.loading = true;

    Logger.debug('[Login] authenticating: ', this.f.username.value, this.f.password.value);

    this.auth.login(this.f.username.value, this.f.password.value)
      .pipe(first())
      .subscribe(
        data => {

          Logger.debug('[Login] authenticated! returning to: ', this.returnUrl, data);
          Logger.debug('[Login] query params:', this.queryParams);

          this.router.navigate(
            [this.returnUrl],
            {
              queryParams: this.queryParams,
              queryParamsHandling: 'merge'
            });
        },
        error => {

          this.alerts.error(error.error.message, {autoClose: true});

          Logger.error('[Login] authentication error: ', error.error.message);

          this.loading = false;
        });
  }

}
